import {
  Mutation,
  MutationCache,
  MutationMeta,
  QueryCache,
  QueryClient,
  QueryMeta,
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import { TFunction } from 'i18next';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import {
  handleGenericMutationErrors,
  handleGenericMutationSuccess,
  handleGenericQueryErrors,
  handleHTTPStatus,
} from './utils';
import FetchError from './utils/FetchError';

interface CustomMutationMeta extends QueryMeta {
  onSuccess?: (
    data: unknown,
    variables: unknown,
    context: unknown,
    mutation: Mutation<unknown, unknown, unknown, unknown>,
    router: AppRouterInstance,
    t: TFunction<'translation', undefined>,
  ) => void;
  onError?: (
    error: unknown,
    variables: unknown,
    context: unknown,
    mutation: Mutation<unknown, unknown, unknown, unknown>,
    router: AppRouterInstance,
    t: TFunction<'translation', undefined>,
  ) => void;
  errorMessage?: string;
  successMessage?: string;
}
export interface CustomUseMutationOptions<FrontEntity, FetchError, FrontBody>
  extends UseMutationOptions<FrontEntity, FetchError, FrontBody> {
  meta?: CustomMutationMeta;
}

interface CustomQueryMeta extends MutationMeta {
  onSuccess?: (
    data: unknown,
    query: unknown,
    router: AppRouterInstance,
    t: TFunction<'translation', undefined>,
  ) => void;
  onError?: (error: unknown, query: unknown, router: AppRouterInstance, t: TFunction<'translation', undefined>) => void;
}
export interface CustomUseQueryOptions<FrontEntity, FetchError>
  extends UseQueryOptions<FrontEntity, FetchError, FrontEntity> {
  meta?: CustomQueryMeta;
}

const defaultRetry = 3;

export const ReactQueryConfig = (router: AppRouterInstance, t: TFunction<'translation', undefined>): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        retry: (failureCount: number, error: Error) => {
          handleHTTPStatus((error as FetchError).response?.status, router);
          return failureCount < defaultRetry;
        },
      },
      mutations: {
        retry: (failureCount: number, error: Error) => {
          handleHTTPStatus((error as FetchError).response?.status, router);
          return failureCount < defaultRetry;
        },
      },
    },
    mutationCache: new MutationCache({
      onSuccess: (data, variables, context, mutation) => {
        if (mutation?.meta?.onSuccess) {
          (mutation.meta as CustomMutationMeta).onSuccess?.(data, variables, context, mutation, router, t);
        } else {
          void handleGenericMutationSuccess(data, variables, context, mutation, router, t);
        }
      },
      onError: (error, variables, context, mutation) => {
        if (mutation?.meta?.onError) {
          (mutation.meta as CustomMutationMeta).onError?.(error as FetchError, variables, context, mutation, router, t);
        } else {
          handleGenericMutationErrors(error as FetchError, variables, context, mutation, router, t);
        }
      },
    }),
    queryCache: new QueryCache({
      onSuccess: (data, query) => {
        if (query?.meta?.onSuccess) {
          (query.meta as CustomQueryMeta).onSuccess?.(data, query, router, t);
        }
      },
      onError: (error, query) => {
        if (query?.meta?.onError) {
          (query.meta as CustomQueryMeta).onError?.(error as FetchError, query, router, t);
        } else {
          void handleGenericQueryErrors(error as FetchError, query, router, t);
        }
      },
    }),
  });
